/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearPaymentMethodFormErrors,
  selectPaymentMethodFieldErrors,
  selectPaymentMethodFormError,
  updateCreditCard,
} from '../../../../redux/slices/billingSlice/billingSlice';

function PaymentMethodModal({
  show,
  handleClose,
  cardOnFile,
}) {
  const dispatch = useDispatch();

  const fieldErrors = useSelector(selectPaymentMethodFieldErrors);
  const formError = useSelector(selectPaymentMethodFormError);

  const [form, setForm] = useState({
    cardNumber: '',
    cardExpirationMonth: '',
    cardExpirationYear: '',
    cardCvv: '',
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cardExpirationMonth = '';
    let cardExpirationYear = '';
    if (cardOnFile && cardOnFile.expirationDate) {
      const expDate = cardOnFile.expirationDate.toString();
      cardExpirationMonth = expDate.substring(0, 2);
      cardExpirationYear = expDate.substring(2, 4);
    }
    setForm({
      cardNumber: cardOnFile && cardOnFile.cardNumber ? cardOnFile.cardNumber : '',
      cardExpirationMonth,
      cardExpirationYear,
      cardCvv: '',
    });

    dispatch(clearPaymentMethodFormErrors());
  }, [show]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        cardExpirationYear: form.cardExpirationYear.slice(-2),
      };

      await dispatch(updateCreditCard(payload)).unwrap();
      setSubmitting(false);
      handleClose();
      window.location.reload();
    } catch (err) {
      setSubmitting(false);
    }
  };

  const months = [
    '01 - Jan', '02 - Feb', '03 - Mar', '04 - Apr',
    '05 - May', '06 - Jun', '07 - Jul', '08 - Aug',
    '09 - Sep', '10 - Oct', '11 - Nov', '12 - Dec',
  ];

  const years = Array.from({ length: 12 }, (_, i) => {
    const fullYear = new Date().getFullYear() + i;
    return {
      label: fullYear.toString(),
      value: fullYear.toString().slice(-2),
    };
  });

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New Payment Method</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* FORM LEVEL ERROR */}
        {formError && (
          <div className="alert alert-danger">
            {formError}
          </div>
        )}

        {/* Card Number */}
        <div className="form-group">
          <label>Card Number</label>
          <input
            className={`form-control ${fieldErrors.cardNumber ? 'is-invalid' : ''}`}
            value={form.cardNumber}
            onChange={(e) => handleChange('cardNumber', e.target.value)}
            placeholder=""
          />
          {fieldErrors.cardNumber && (
            <div className="invalid-feedback">
              {fieldErrors.cardNumber}
            </div>
          )}
        </div>

        {/* Expiration */}
        <div className="form-group">
          <label>Expiration Date</label>
          <div className="d-flex gap-2">
            <select
              className={`form-control ${fieldErrors.cardExpirationMonth ? 'is-invalid' : ''}`}
              value={form.cardExpirationMonth}
              onChange={(e) => handleChange('cardExpirationMonth', e.target.value)}
            >
              <option value="">Month</option>
              {months.map((m) => (
                <option key={m} value={m.split(' - ')[0]}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className={`form-control ${fieldErrors.cardExpirationYear ? 'is-invalid' : ''}`}
              value={form.cardExpirationYear}
              onChange={(e) => handleChange('cardExpirationYear', e.target.value)}
            >
              <option value="">Year</option>
              {years.map((y) => (
                <option key={y.label} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>

          {(fieldErrors.cardExpirationMonth || fieldErrors.cardExpirationYear) && (
            <div className="invalid-feedback d-block">
              {fieldErrors.cardExpirationMonth || fieldErrors.cardExpirationYear}
            </div>
          )}
        </div>

        {/* CVV */}
        <div className="form-group">
          <label>CVV</label>
          <input
            className={`form-control ${fieldErrors.cardCvv ? 'is-invalid' : ''}`}
            value={form.cardCvv}
            onChange={(e) => handleChange('cardCvv', e.target.value)}
            placeholder=""
            maxLength={4}
          />
          {fieldErrors.cardCvv && (
            <div className="invalid-feedback">
              {fieldErrors.cardCvv}
            </div>
          )}
        </div>

        <p className="text-muted mt-3">
          By submitting this form, you authorize AVTECH Software, Inc. to use this
          payment method for future renewals. You may cancel renewals at any time.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save'}
        </button>

        <button
          type="button"
          className="btn btn-link"
          onClick={handleClose}
        >
          cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
}

PaymentMethodModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  cardOnFile: PropTypes.shape({
    cardNumber: PropTypes.string,
    expirationDate: PropTypes.string,
  }),
};

export default PaymentMethodModal;
